import React, { useState, useEffect } from 'react';
import { 
  BarChart3, FileText, DollarSign, Car, TrendingUp, 
  Users, Clock, ArrowUpRight, Database,
  PlusCircle, Eye, RefreshCw, Loader2, Award
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../api/client';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRecords: 0,
    totalInvoices: 0,
    topBrands: [],  // Changé pour stocker un tableau
    recentRecords: [],
    monthlyGrowth: 0
  });

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Utiliser le nouvel endpoint backend
      const response = await fetch(`${API_BASE_URL}/dashboard-stats`);
      const data = await response.json();

      setStats({
        totalRecords: data.total_records,
        totalInvoices: data.total_invoices,
        topBrands: data.top_brands || [],
        recentRecords: data.recent_records || [],
        monthlyGrowth: data.monthly_growth || 0
      });
    } catch (error) {
      console.error('Erreur lors du chargement du dashboard:', error);
      // Fallback vers l'ancienne logique si le nouvel endpoint échoue
      try {
        // Récupérer tous les dossiers
        const recordsResponse = await fetch(`${API_BASE_URL}/records`);
        const recordsData = await recordsResponse.json();
        const allRecords = recordsData.records || [];

        // Calculer les statistiques
        const brandsCount = {};
        let totalInvoices = 0;
        
        // Pour chaque dossier, récupérer les factures
        for (const record of allRecords) {
          // Compter les marques
          const brand = record.marque || 'Inconnue';
          brandsCount[brand] = (brandsCount[brand] || 0) + 1;

          // Récupérer les factures de ce dossier
          try {
            const invoicesResponse = await fetch(`${API_BASE_URL}/dossier/${record.id}/factures`);
            if (invoicesResponse.ok) {
              const invoicesData = await invoicesResponse.json();
              totalInvoices += invoicesData.factures?.length || 0;
            }
          } catch (err) {
            console.log(`Pas de factures pour le dossier ${record.id}`);
          }
        }

        // Trouver les 3 marques les plus fréquentes
        const sortedBrands = Object.entries(brandsCount)
          .filter(([brand]) => brand !== 'Inconnue')
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([brand, count]) => ({ brand, count }));

        // Récents dossiers (5 derniers)
        const recentRecords = allRecords
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5);

        // Calcul croissance mensuelle (simplifiée)
        const monthlyGrowth = allRecords.length > 10 ? 12 : 0;

        setStats({
          totalRecords: allRecords.length,
          totalInvoices,
          topBrands: sortedBrands,
          recentRecords,
          monthlyGrowth
        });
      } catch (fallbackError) {
        console.error('Erreur dans le fallback:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <span className={`text-sm font-semibold ${stats.monthlyGrowth > 0 ? 'text-green-600' : 'text-gray-500'}`}>
          {stats.monthlyGrowth > 0 && `+${stats.monthlyGrowth}%`}
        </span>
      </div>
      <h3 className="text-3xl font-bold text-gray-800 mb-1">{value}</h3>
      <p className="text-gray-600 text-sm">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );

  // Nouveau composant pour afficher les 3 marques
  const TopBrandsCard = () => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg bg-purple-500`}>
          <Award className="w-6 h-6 text-white" />
        </div>
        <span className={`text-sm font-semibold ${stats.monthlyGrowth > 0 ? 'text-green-600' : 'text-gray-500'}`}>
          {stats.monthlyGrowth > 0 && `+${stats.monthlyGrowth}%`}
        </span>
      </div>
      
      <h3 className="text-2xl font-bold text-gray-800 mb-4">Top 3 des Marques</h3>
      
      {stats.topBrands.length > 0 ? (
        <div className="space-y-3">
          {stats.topBrands.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white rounded-full">
                  <span className="text-purple-600 font-bold">{index + 1}</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{item.brand}</p>
                  <p className="text-sm text-gray-500">{item.count} dossiers</p>
                </div>
              </div>
              <div className="w-16 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full" 
                  style={{ 
                    width: `${Math.min((item.count / Math.max(stats.totalRecords, 1)) * 100, 100)}%` 
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500">
          <Car className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p>Aucune donnée de marque</p>
        </div>
      )}
      
      <p className="text-gray-600 text-sm mt-4">Marques les plus traitées</p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-blue-600" />
                Tableau de Bord
              </h1>
              <p className="text-gray-600 mt-2">
                Vue d'ensemble de l'activité et des statistiques
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/records')}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Eye className="w-4 h-4" />
                Voir les Dossiers
              </button>
              <button
                onClick={() => navigate('/upload')}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusCircle className="w-4 h-4" />
                Ajouter un Dossier
              </button>
            </div>
          </div>
        </div>

        {/* Statistiques principales - 3 cartes au lieu de 4 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard
            title="Dossiers Traités"
            value={stats.totalRecords}
            icon={Database}
            color="bg-blue-500"
            subtitle="Total des expertises analysées"
          />
          <StatCard
            title="Factures Traitées"
            value={stats.totalInvoices}
            icon={DollarSign}
            color="bg-green-500"
            subtitle="Factures extraites"
          />
          {/* Remplacé par la nouvelle carte TopBrandsCard */}
          <TopBrandsCard />
          {/* Supprimé: la carte Alertes Fraude */}
        </div>

        {/* Le reste du code reste inchangé */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Dossiers récents */}
          <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                Dossiers Récents
              </h2>
              <button
                onClick={fetchDashboardData}
                className="text-gray-500 hover:text-gray-700"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            
            {stats.recentRecords.length > 0 ? (
              <div className="space-y-4">
                {stats.recentRecords.map((record) => (
                  <div key={record.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{record.original_filename}</p>
                        <p className="text-sm text-gray-500">
                          {record.marque ? `${record.marque} • ` : ''}
                          {record.immatriculation || 'Pas d\'immatriculation'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {new Date(record.created_at).toLocaleDateString()}
                      </span>
                      <button
                        onClick={() => navigate(`/records/${record.id}`)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Voir →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Aucun dossier pour le moment</p>
                <button
                  onClick={() => navigate('/upload')}
                  className="mt-4 text-blue-600 hover:text-blue-800"
                >
                  Commencez par ajouter un dossier
                </button>
              </div>
            )}
          </div>

          {/* Actions rapides & Info */}
          <div className="space-y-6">
            {/* Actions rapides */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Actions Rapides</h2>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/upload')}
                  className="w-full flex items-center gap-3 p-3 text-left bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <PlusCircle className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Ajouter un nouveau dossier</p>
                    <p className="text-sm opacity-75">Analyse automatique par IA</p>
                  </div>
                </button>
                <button
                  onClick={() => navigate('/tables')}
                  className="w-full flex items-center gap-3 p-3 text-left bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <FileText className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Extraire des tableaux</p>
                    <p className="text-sm opacity-75">Factures, devis, pièces</p>
                  </div>
                </button>
                <button
                  onClick={() => navigate('/records')}
                  className="w-full flex items-center gap-3 p-3 text-left bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <Database className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Consulter tous les dossiers</p>
                    <p className="text-sm opacity-75">{stats.totalRecords} disponibles</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Statistiques avancées */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
              <h2 className="text-xl font-bold mb-4">Performance du Mois</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Nouveaux dossiers</span>
                    <span className="font-bold">+{stats.monthlyGrowth}%</span>
                  </div>
                  <div className="w-full bg-blue-400 rounded-full h-2">
                    <div 
                      className="bg-white h-2 rounded-full" 
                      style={{ width: `${Math.min(stats.monthlyGrowth, 100)}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Taux d'extraction</span>
                    <span className="font-bold">98%</span>
                  </div>
                  <div className="w-full bg-blue-400 rounded-full h-2">
                    <div className="bg-green-400 h-2 rounded-full" style={{ width: '98%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Dossiers analysés</span>
                    <span className="font-bold">{stats.totalRecords}</span>
                  </div>
                  <div className="w-full bg-blue-400 rounded-full h-2">
                    <div 
                      className="bg-yellow-400 h-2 rounded-full" 
                      style={{ width: `${Math.min(stats.totalRecords * 2, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Légende */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Dashboard mis à jour en temps réel • Données actualisées à chaque chargement</p>
        </div>
      </div>
    </div>
  );
}